/**
 * useIndicatorSeries — per-bar indicator values for one (screener, symbol, date).
 *
 * Follows the page idiom (useEffect + api.get + local state; there is no
 * react-query here) and adds two things this view needs:
 *
 *   - a session cache, because re-selecting a signal row is the common action
 *   - a price-only fallback, so a screener that fails to load or replay still
 *     renders its chart instead of an empty panel
 */
import { useEffect, useRef, useState } from 'react';
import api from '../api/client';

const EMPTY = {
  ohlcv: [], series: [], evaluations: [], blockers: {}, warnings: [], meta: {},
};

/** −6 months / +2 months around the signal, clamped to today. */
function windowFor(signalDate) {
  const signal = new Date(signalDate);
  const from = new Date(signal);
  from.setMonth(from.getMonth() - 6);
  const to = new Date(signal);
  to.setMonth(to.getMonth() + 2);
  const today = new Date();
  return {
    from: from.toISOString().slice(0, 10),
    to: (to > today ? today : to).toISOString().slice(0, 10),
  };
}

export default function useIndicatorSeries(screenerId, symbol, signalDate, { deps = false } = {}) {
  const [state, setState] = useState({ ...EMPTY, loading: false, error: null });
  const cache = useRef(new Map());

  useEffect(() => {
    if (!screenerId || !symbol || !signalDate) {
      setState({ ...EMPTY, loading: false, error: null });
      return undefined;
    }

    const bare = symbol.replace('.NS', '');
    const key = `${screenerId}|${bare}|${signalDate}|${deps ? 1 : 0}`;
    const hit = cache.current.get(key);
    if (hit) {
      setState({ ...hit, loading: false });
      return undefined;
    }

    const controller = new AbortController();
    const { from, to } = windowFor(signalDate);

    // Hold the previous render while the next one loads rather than blanking
    // the panel — the frame stays, the content fades.
    setState((prev) => ({ ...prev, loading: true, error: null }));

    api
      .get(`/screeners/${screenerId}/indicator-series`, {
        params: { symbol: bare, from, to, ...(deps ? { deps: 1 } : {}) },
        signal: controller.signal,
      })
      .then((res) => {
        const payload = { ...EMPTY, ...res.data, error: null };
        cache.current.set(key, payload);
        setState({ ...payload, loading: false });
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        const error =
          err.response?.data?.error || 'Could not compute indicators';
        // Fall back to price alone, so the chart is still usable and the
        // reason is visible rather than silent. Deliberately not cached: a
        // failure should be retried by re-selecting the row, not remembered
        // for the rest of the session.
        api
          .get(`/sync/ohlcv/${bare}`, { params: { from, to } })
          .then((res) =>
            setState({ ...EMPTY, ohlcv: res.data || [], error, loading: false })
          )
          .catch(() => setState({ ...EMPTY, loading: false, error }));
      });

    return () => controller.abort();
  }, [screenerId, symbol, signalDate, deps]);

  return state;
}
